import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip, Divider, Stack, IconButton, Tooltip, Card, CardMedia, CircularProgress, Link } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PersonIcon from '@mui/icons-material/Person';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PDFViewer from '../ChatWindow/PDFViewer';
import FileViewer from '../ChatWindow/FileViewer';
import { supabase } from '../../lib/supabase';
import { DocumentService, DocumentInfo } from '../../services/documentService';
import { UserService } from '../../services/userService';
import { parseBoldTextInline } from '../../utils/textParser';

// Normalize various document field shapes to a simple string[] of URLs/paths
function normalizeDocuments(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {}
    if (raw.includes(',')) return raw.split(',').map((s: string) => s.trim()).filter(Boolean);
    return [raw];
  }
  if (typeof raw === 'object') {
    const values = Object.values(raw).filter(Boolean) as string[];
    return values.length > 0 ? values : [];
  }
  return [];
}

const isHttpUrl = (s: string) => /^https?:\/\//i.test(s);

const getSignedUrlForApplicationObject = async (pathOrUrl: string): Promise<string | null> => {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return null;
  if (isHttpUrl(raw)) return raw;
  try {
    const { data, error } = await supabase.storage
      .from('applications')
      .createSignedUrl(raw, 3600);
    if (error) return null;
    return data?.signedUrl ?? null;
  } catch {
    return null;
  }
};

const fileNameFromPath = (p: string) => {
  const last = String(p || '').split('/').pop() || 'document';
  return last.split('?')[0] || 'document';
};

const asStoragePaths = (raw: any): string[] => {
  return normalizeDocuments(raw)
    .map((s) => String(s || '').trim())
    .filter(Boolean);
};

const guessType = (urlOrName: string): string => {
  const lower = urlOrName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.csv')) return 'text/csv';
  if (lower.match(/\.(xlsx|xls)$/)) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (lower.match(/\.(png|jpg|jpeg|webp)$/)) return 'image/*';
  if (lower.match(/\.(doc|docx)$/)) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
};

type ApplicationDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  application: any | null;
  onViewPosting?: (productId: string) => void;
  onViewProfile?: (userId: string) => void;
  onStatusUpdate?: (applicationId: string, status: 'accepted' | 'rejected' | 'submitted' | 'review') => void;
};

const ApplicationDetailsModal: React.FC<ApplicationDetailsModalProps> = ({ open, onClose, application, onViewPosting, onViewProfile, onStatusUpdate }) => {
  const [docViewerOpen, setDocViewerOpen] = React.useState(false);
  const [docViewerFile, setDocViewerFile] = React.useState<{ url: string; name: string; type: string } | null>(null);
  const [documents, setDocuments] = React.useState<DocumentInfo[]>([]);
  const [loadingDocuments, setLoadingDocuments] = React.useState(false);
  const [applicantProfile, setApplicantProfile] = React.useState<any>(null);
  const [loadingProfile, setLoadingProfile] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [updatingStatus, setUpdatingStatus] = React.useState(false);

  const loadApplicantProfile = async (applicantId: string) => {
    setLoadingProfile(true);
    try {
      const profile = await UserService.getUserProfile(applicantId);
      setApplicantProfile(profile);
    } catch (error) {
      console.error('Error loading applicant profile:', error);
      setApplicantProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleStatusUpdate = async (status: 'accepted' | 'rejected' | 'submitted' | 'review') => {
    if (!application?.id || !onStatusUpdate) return;
    
    setUpdatingStatus(true);
    try {
      await onStatusUpdate(application.id, status);
    } catch (error) {
      console.error('Error updating application status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  React.useEffect(() => {
    if (!open || !application) return;
    
    const loadDocuments = async () => {
      setLoadingDocuments(true);
      try {
        const merged: DocumentInfo[] = [];
        const seen = new Set<string>();

        const push = (doc: DocumentInfo) => {
          const key = String(doc?.id || doc?.file_path || doc?.url || '').trim();
          if (!key) return;
          if (seen.has(key)) return;
          seen.add(key);
          merged.push(doc);
        };

        // Check if documents is an array of UUIDs (new format)
        if (Array.isArray(application.documents) && application.documents.length > 0) {
          // Check if first element looks like a UUID (new format)
          const firstDoc = application.documents[0];
          if (typeof firstDoc === 'string' && firstDoc.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
            // New format: array of UUIDs
            console.log('Loading documents by UUIDs:', application.documents);
            const documentInfos = await DocumentService.getDocumentsByIds(application.documents);
            for (const d of documentInfos) push(d);
          }
        }

        // If application.documents is already an array of objects with url
        if (Array.isArray(application.documents) && application.documents.length > 0) {
          const first = application.documents[0];
          if (first && typeof first === 'object' && (first.url || first.file_path)) {
            for (const entry of application.documents) {
              const url = String((entry as any)?.url || '').trim();
              const fp = String((entry as any)?.file_path || '').trim();
              const name = String((entry as any)?.name || (entry as any)?.file_name || fileNameFromPath(url || fp) || 'document');
              const type = String((entry as any)?.mimeType || (entry as any)?.mime_type || (entry as any)?.file_type || guessType(name));
              if (url) {
                push({
                  id: String((entry as any)?.id || url),
                  file_name: name,
                  file_path: fp || url,
                  url,
                  file_type: type,
                  file_size: Number((entry as any)?.fileSize || (entry as any)?.file_size || 0) || 0,
                  document_type: String((entry as any)?.type || (entry as any)?.document_type || 'other') as any,
                  is_public: false,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  user_id: String((entry as any)?.user_id || ''),
                } as any);
              }
            }
          }
        }
        
        // Fallback to old format: treat as URLs/paths
        console.log('Loading documents using legacy format');
        const legacyDocs = normalizeDocuments(application.documents);
        // Convert legacy format to DocumentInfo-like objects
        const convertedDocs = legacyDocs.map((url: string) => ({
          id: url,
          file_name: url.split('/').pop() || 'document',
          file_path: url,
          url: url,
          file_type: guessType(url),
          file_size: 0,
          document_type: 'other',
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_id: ''
        }));

        for (const d of convertedDocs) push(d as any);

        // Supabase applications bucket paths (resume_url + additional_documents_urls)
        const storagePaths = [
          ...asStoragePaths((application as any)?.resume_url),
          ...asStoragePaths((application as any)?.additional_documents_urls),
        ];

        for (const p of storagePaths) {
          const signed = await getSignedUrlForApplicationObject(p);
          if (!signed) continue;
          push({
            id: p,
            file_name: fileNameFromPath(p),
            file_path: p,
            url: signed,
            file_type: guessType(p),
            file_size: 0,
            document_type: 'other',
            is_public: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: '',
          } as any);
        }

        setDocuments(merged);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    // Load documents and applicant profile in parallel
    loadDocuments();
    
    // Load applicant profile if we have applicant ID
    if (application?.applicant?.id) {
      loadApplicantProfile(application.applicant.id);
    }
  }, [open, application]);

  if (!application) return null;

  const fullName = `${application?.applicant?.first_name || ''} ${application?.applicant?.last_name || ''}`.trim();

  const handleOpenDocument = async (url: string) => {
    const lower = url.toLowerCase();
    const name = url.split('/').pop() || 'document';
    
    // Check if this is a Supabase storage URL that needs a fresh signed URL
    if (url.includes('supabase.co') && url.includes('/storage/')) {
      try {
        // Extract the file path from the URL
        const urlParts = url.split('/storage/v1/object/');
        if (urlParts.length > 1) {
          const fullPath = urlParts[1];
          const pathParts = fullPath.split('/');
          const bucket = pathParts[0];
          const filePath = pathParts.slice(1).join('/');
          
          // Create a fresh signed URL
          const { data: urlData, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(filePath, 3600); // 1 hour expiry
          
          if (error) {
            console.error('Error creating signed URL:', error);
            // Fallback to original URL
            setDocViewerFile({ url, name, type: 'application/pdf' });
            setDocViewerOpen(true);
            return;
          }
          
          if (urlData?.signedUrl) {
            url = urlData.signedUrl;
          }
        }
      } catch (err) {
        console.error('Error refreshing signed URL:', err);
        // Continue with original URL
      }
    }
    
    // Best-effort type detection from extension
    let type = 'application/octet-stream';
    if (lower.endsWith('.pdf')) type = 'application/pdf';
    else if (lower.endsWith('.csv')) type = 'text/csv';
    else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    else if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) type = 'image/*';
    else if (lower.endsWith('.doc') || lower.endsWith('.docx')) type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    setDocViewerFile({ url, name, type });
    setDocViewerOpen(true);
  };

  const handleDownload = async (url: string, name?: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = name || (url.split('/').pop() || 'document');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // Ignore failures silently
    }
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "lg"} 
      fullWidth={!isFullscreen}
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          borderRadius: isFullscreen ? 0 : 4,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        pt: 3,
        px: 3,
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '16px 16px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: 'white' }}>{application?.product?.name || 'Application'}</Typography>
            <Chip label={(application?.status || 'submitted').toUpperCase()} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.35)' }} />
            {application?.applicant?.alias && (
              <Chip size="small" label={`@${application.applicant.alias}`} variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.35)' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {new Date(application?.created_at).toLocaleString()}
            </Typography>
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
              <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 4, background: 'rgba(255, 255, 255, 0.7)', minHeight: 400 }}>
        <Stack spacing={3}>
          <Card sx={{ 
            p: 3, 
            bgcolor: 'rgba(255, 255, 255, 0.9)', 
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
              Applicant Information
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {fullName && (
                <Typography variant="h6" fontWeight={700} color="primary.main">{fullName}</Typography>
              )}
              {application?.applicant?.alias && (
                <Chip 
                  size="medium" 
                  label={`@${application.applicant.alias}`} 
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {application?.applicant?.id && (
                <Button
                  size="medium"
                  variant="contained"
                  onClick={() => onViewProfile && onViewProfile(application.applicant.id)}
                  startIcon={<OpenInNewIcon />}
                  sx={{ 
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 3
                  }}
                >
                  View Profile
                </Button>
              )}
            </Box>
          </Card>

          {/* Professional Background Section */}
          {applicantProfile && (applicantProfile.work_experience || applicantProfile.education) && (
            <Card sx={{ 
              p: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.9)', 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3, fontWeight: 600 }}>
                Professional Background
              </Typography>
              
              <Stack spacing={3}>
                {applicantProfile.work_experience && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <WorkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Work Experience
                      </Typography>
                    </Box>
                    <Card sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(14, 165, 233, 0.05)', 
                      borderRadius: 2,
                      border: '1px solid rgba(14, 165, 233, 0.1)'
                    }}>
                      {parseBoldTextInline(applicantProfile.work_experience, 'body2', {
                        lineHeight: 1.6,
                        color: 'text.primary'
                      })}
                    </Card>
                  </Box>
                )}

                {applicantProfile.education && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <SchoolIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        Education
                      </Typography>
                    </Box>
                    <Card sx={{ 
                      p: 2.5, 
                      bgcolor: 'rgba(139, 92, 246, 0.05)', 
                      borderRadius: 2,
                      border: '1px solid rgba(139, 92, 246, 0.1)'
                    }}>
                      {parseBoldTextInline(applicantProfile.education, 'body2', {
                        lineHeight: 1.6,
                        color: 'text.primary'
                      })}
                    </Card>
                  </Box>
                )}
              </Stack>
            </Card>
          )}

          {/* Contact Information Section */}
          {applicantProfile && (applicantProfile.location || applicantProfile.phone || applicantProfile.website || applicantProfile.social_media_links) && (
            <Card sx={{ 
              p: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.9)', 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3, fontWeight: 600 }}>
                Contact Information
              </Typography>
              
              <Stack spacing={2}>
                {applicantProfile.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {applicantProfile.location}
                    </Typography>
                  </Box>
                )}

                {applicantProfile.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {applicantProfile.phone}
                    </Typography>
                  </Box>
                )}

                {applicantProfile.website && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LanguageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Link 
                      href={applicantProfile.website.startsWith('http') ? applicantProfile.website : `https://${applicantProfile.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {applicantProfile.website}
                    </Link>
                  </Box>
                )}

                {applicantProfile.social_media_links && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LinkedInIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    {(applicantProfile.social_media_links.linkedin || applicantProfile.social_media_links.linkedin_url) && (
                      <Link 
                        href={applicantProfile.social_media_links.linkedin || applicantProfile.social_media_links.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: '#0077b5',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        LinkedIn Profile
                      </Link>
                    )}
                  </Box>
                )}
              </Stack>
            </Card>
          )}

          {/* Bio Section */}
          {applicantProfile?.bio && (
            <Card sx={{ 
              p: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.9)', 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  About
                </Typography>
              </Box>
              <Card sx={{ 
                p: 2.5, 
                bgcolor: 'rgba(16, 185, 129, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(16, 185, 129, 0.1)'
              }}>
                {parseBoldTextInline(applicantProfile.bio, 'body2', {
                  lineHeight: 1.6,
                  color: 'text.primary'
                })}
              </Card>
            </Card>
          )}

          {application?.letter && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Application Letter
              </Typography>
              <Card sx={{ 
                p: 3, 
                bgcolor: 'rgba(255, 255, 255, 0.8)', 
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}>
                {parseBoldTextInline(application.letter, 'body1', {
                  lineHeight: 1.6,
                  color: 'text.primary'
                })}
              </Card>
            </Box>
          )}

          <Card sx={{ 
            p: 3, 
            bgcolor: 'rgba(255, 255, 255, 0.9)', 
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
              Attached Documents
            </Typography>
            {loadingDocuments ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">Loading documents…</Typography>
              </Box>
            ) : documents.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                px: 3, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                border: '2px dashed #cbd5e1'
              }}>
                <DescriptionIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  No documents attached
                </Typography>
              </Box>
            ) : (
              (() => {
                const normalizeKey = (d: DocumentInfo) => {
                  const nm = String(d?.file_name || '').toLowerCase();
                  const fp = String((d as any)?.file_path || '').toLowerCase();
                  const url = String((d as any)?.url || '').toLowerCase();
                  return `${nm}|${fp}|${url}`;
                };

                const isResumeDoc = (d: DocumentInfo) => {
                  const nm = String(d?.file_name || '').toLowerCase();
                  const fp = String((d as any)?.file_path || '').toLowerCase();
                  return d?.document_type === 'cv' || nm.includes('resume') || nm.includes('cv') || fp.includes('/resume/') || fp.includes('/cv/');
                };

                const resumeDocs = documents.filter(isResumeDoc);
                const additionalDocs = documents.filter((d) => !isResumeDoc(d));

                const renderDocCard = (doc: DocumentInfo, idx: number, role: 'resume' | 'additional') => {
                  const url = doc.url || '';
                  const name = doc.file_name || `Document-${idx + 1}`;
                  const docType = doc.document_type || 'other';
                  const description = doc.description || '';
                  
                  const lower = (url || '').toLowerCase();
                  const isPdf = lower.endsWith('.pdf') || doc.file_type === 'application/pdf';
                  const isImage = lower.match(/\.(png|jpg|jpeg|webp)$/) || doc.file_type?.startsWith('image/');
                  const isDoc = lower.match(/\.(doc|docx)$/) || doc.file_type?.includes('word');
                  
                  let icon = <InsertDriveFileIcon sx={{ fontSize: 32 }} />;
                  let bgColor = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                  let iconColor = '#64748b';
                  
                  // Color code by document type
                  if (role === 'resume' || docType === 'cv') {
                    icon = <DescriptionIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, rgba(2,132,199,0.12) 0%, rgba(2,132,199,0.06) 100%)';
                    iconColor = '#0284c7';
                  } else if (docType === 'cover_letter') {
                    icon = <DescriptionIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
                    iconColor = '#2563eb';
                  } else if (docType === 'portfolio') {
                    icon = <ImageIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)';
                    iconColor = '#16a34a';
                  } else if (docType === 'certificate') {
                    icon = <PictureAsPdfIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)';
                    iconColor = '#be185d';
                  } else if (isPdf) {
                    icon = <PictureAsPdfIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
                    iconColor = '#dc2626';
                  } else if (isImage) {
                    icon = <ImageIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
                    iconColor = '#0284c7';
                  } else if (isDoc) {
                    icon = <DescriptionIcon sx={{ fontSize: 32 }} />;
                    bgColor = 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)';
                    iconColor = '#16a34a';
                  }

                  const shortName = (name || '').length > 22 ? (name || '').substring(0, 22) + '...' : (name || '');

                  const handleDocumentClick = () => {
                    if (isPdf) {
                      // Open PDF in new tab
                      window.open(url, '_blank');
                    } else {
                      // For other files, use the existing viewer
                      handleOpenDocument(url);
                    }
                  };

                  return (
                    <Card 
                      key={`${normalizeKey(doc)}:${idx}`}
                      sx={{ 
                        position: 'relative',
                        borderRadius: 3, 
                        overflow: 'hidden', 
                        border: '1px solid rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: bgColor,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                          borderColor: iconColor,
                        }
                      }}
                      onClick={handleDocumentClick}
                    >
                      <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                        <Chip
                          size="small"
                          label={role === 'resume' ? 'Resume / CV' : 'Additional'}
                          sx={{
                            height: 22,
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            background: 'rgba(255,255,255,0.75)',
                          }}
                        />
                      </Box>
                      {isImage ? (
                        <CardMedia 
                          component="img" 
                          image={url} 
                          alt={name} 
                          sx={{ 
                            height: 140, 
                            objectFit: 'cover',
                            background: bgColor
                          }} 
                        />
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: 140, 
                          background: bgColor,
                          position: 'relative'
                        }}>
                          {icon}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: iconColor, 
                              fontWeight: 700, 
                              mt: 1,
                              textAlign: 'center',
                              px: 1
                            }}
                          >
                            {isPdf ? 'PDF' : isDoc ? 'DOC' : isImage ? 'IMAGE' : 'FILE'}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ 
                        p: 2, 
                        background: 'rgba(255, 255, 255, 0.92)',
                        backdropFilter: 'blur(10px)',
                        borderTop: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700, 
                            color: '#1e293b',
                            mb: description ? 0.5 : 1,
                            textAlign: 'center',
                            lineHeight: 1.2
                          }}
                          title={name}
                        >
                          {shortName}
                        </Typography>
                        {description ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', fontWeight: 600 }}>
                            {description}
                          </Typography>
                        ) : null}

                        {/* Document Type Badge */}
                        {docType !== 'other' && (
                          <Box sx={{ mb: 1, textAlign: 'center' }}>
                            <Chip 
                              label={docType.replace('_', ' ').toUpperCase()} 
                              size="small" 
                              sx={{ 
                                fontSize: '0.7rem',
                                height: 20,
                                backgroundColor: `${iconColor}20`,
                                color: iconColor,
                                fontWeight: 600
                              }} 
                            />
                          </Box>
                        )}
                        
                        {/* Description */}
                        {description && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#64748b',
                              textAlign: 'center',
                              display: 'block',
                              mb: 1,
                              lineHeight: 1.2
                            }}
                          >
                            {description}
                          </Typography>
                        )}
                        
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          gap: 1 
                        }}>
                          <Tooltip title={isPdf ? "Open in new tab" : "Open document"}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentClick();
                              }}
                              sx={{ 
                                color: iconColor,
                                '&:hover': {
                                  backgroundColor: `${iconColor}15`,
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Download">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(url, name);
                              }}
                              sx={{ 
                                color: '#64748b',
                                '&:hover': {
                                  backgroundColor: '#f1f5f9',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Card>
                  );
                };

                const renderGrid = (items: DocumentInfo[], role: 'resume' | 'additional') => (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: 2,
                  }}>
                    {items.map((doc, idx) => renderDocCard(doc, idx, role))}
                  </Box>
                );

                return (
                  <Stack spacing={2.5}>
                    {resumeDocs.length > 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                            Resume / CV
                          </Typography>
                          <Chip size="small" color="primary" label={`${resumeDocs.length}`} />
                        </Box>
                        {renderGrid(resumeDocs, 'resume')}
                      </Box>
                    )}

                    {additionalDocs.length > 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                            Additional Documents
                          </Typography>
                          <Chip size="small" label={`${additionalDocs.length}`} />
                        </Box>
                        {renderGrid(additionalDocs, 'additional')}
                      </Box>
                    )}
                  </Stack>
                );
              })()
            )}
          </Card>

          {application?.product_id && (
            <Card sx={{ 
              p: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.9)', 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Related Posting
              </Typography>
              <Button
                variant="contained"
                onClick={() => onViewPosting && onViewPosting(application.product_id)}
                startIcon={<OpenInNewIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5
                }}
              >
                Open Posting
              </Button>
            </Card>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '0 0 16px 16px',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {onStatusUpdate && (
            <>
              <Button 
                onClick={() => handleStatusUpdate('accepted')}
                variant="contained"
                disabled={updatingStatus || application?.status === 'accepted'}
                startIcon={<CheckCircleIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(16, 185, 129, 0.3)',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }
                }}
              >
                Accept
              </Button>
              <Button 
                onClick={() => handleStatusUpdate('rejected')}
                variant="contained"
                disabled={updatingStatus || application?.status === 'rejected'}
                startIcon={<CancelIcon />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  },
                  '&:disabled': {
                    background: 'rgba(239, 68, 68, 0.3)',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }
                }}
              >
                Reject
              </Button>
              <Button 
                onClick={() => handleStatusUpdate('submitted')}
                variant="outlined"
                disabled={updatingStatus || application?.status === 'submitted'}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderColor: '#2563eb'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: 'rgba(59, 130, 246, 0.6)'
                  }
                }}
              >
                Mark as Submitted
              </Button>
              <Button 
                onClick={() => handleStatusUpdate('review')}
                variant="outlined"
                disabled={updatingStatus || application?.status === 'review'}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderColor: '#f59e0b',
                  color: '#f59e0b',
                  '&:hover': {
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    borderColor: '#d97706'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(245, 158, 11, 0.3)',
                    color: 'rgba(245, 158, 11, 0.6)'
                  }
                }}
              >
                Mark as Under Review
              </Button>
            </>
          )}
        </Box>
        
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ 
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {docViewerOpen && docViewerFile ? (
      docViewerFile.type === 'application/pdf' ? (
        <PDFViewer
          open={docViewerOpen}
          onClose={() => setDocViewerOpen(false)}
          fileUrl={docViewerFile.url}
          fileName={docViewerFile.name}
        />
      ) : (docViewerFile.type.includes('sheet') || docViewerFile.type.includes('excel') || docViewerFile.name.toLowerCase().endsWith('.csv')) ? (
        <FileViewer
          open={docViewerOpen}
          onClose={() => setDocViewerOpen(false)}
          fileUrl={docViewerFile.url}
          fileName={docViewerFile.name}
          fileType={docViewerFile.type}
        />
      ) : null
    ) : null}
    </>
  );
};

export default ApplicationDetailsModal;


